// Keys a focused control handles itself (a tab list's arrows, the range
// timeline's points), as data: the control reads its keys through
// `controlCommand` and the key guide lists them from the same table.
// The router never dispatches them; they need the focused element.

import { composing, keyLabel, keyName } from './chord';

/** A control's keys: each spec with the command it names, and one description per row for the guide. */
export interface ControlKeymap<C> {
  id: string;
  title: string;
  rows: readonly { keys: readonly (readonly [spec: string, command: C])[]; does: string }[];
}

export interface ControlSpec {
  key: string;
  shift: boolean;
  alt: boolean;
  ctrl: boolean;
  meta: boolean;
}

const MODS = ['Shift', 'Alt', 'Ctrl', 'Meta'] as const;

/** A control key spec: a key (`Enter`, `Space`, `ArrowLeft`) after optional `Shift+`, `Alt+`, `Ctrl+`, `Meta+`. Throws on any other modifier. */
export function parseControlSpec(spec: string): ControlSpec {
  const parts = spec.length > 1 ? spec.split('+') : [spec];
  const name = parts.pop()!;
  for (const m of parts) if (!(MODS as readonly string[]).includes(m)) throw new Error(`control key spec "${spec}": no ${m} modifier`);
  return { key: keyName(name), shift: parts.includes('Shift'), alt: parts.includes('Alt'), ctrl: parts.includes('Ctrl'), meta: parts.includes('Meta') };
}

/** How the guide writes a control key spec (`Shift Enter`, `←`). */
export function controlLabel(spec: string): string {
  const parts = spec.length > 1 ? spec.split('+') : [spec];
  const name = parts.pop()!;
  return [...parts, keyLabel(keyName(name))].join(' ');
}

type KeyLike = Pick<KeyboardEvent, 'key' | 'shiftKey' | 'altKey' | 'ctrlKey' | 'metaKey' | 'isComposing'> & { keyCode?: number };

/**
 * The command a key names to a control, or null. A spec matches when its key
 * is the event's and every modifier it names is held (modifiers it does not
 * name are not checked); of the specs that match, the one naming the most
 * modifiers wins, then the first. Nothing matches during IME composition.
 */
export function controlCommand<C>(k: ControlKeymap<C>, e: KeyLike): C | null {
  if (composing(e)) return null;
  let best: { c: C; n: number } | null = null;
  for (const r of k.rows)
    for (const [spec, c] of r.keys) {
      const s = parseControlSpec(spec);
      if (s.key !== e.key) continue;
      if ((s.shift && !e.shiftKey) || (s.alt && !e.altKey) || (s.ctrl && !e.ctrlKey) || (s.meta && !e.metaKey)) continue;
      const n = +s.shift + +s.alt + +s.ctrl + +s.meta;
      if (!best || n > best.n) best = { c, n };
    }
  return best?.c ?? null;
}
